import gsap from 'gsap'
import ScrollTrigger from 'gsap/ScrollTrigger'
import { useLayoutEffect, useRef, useState, useEffect, useCallback } from 'react'
import { useNavigate } from "react-router-dom"
import { Splide, SplideSlide } from '@splidejs/react-splide'
import '@splidejs/react-splide/css'
import ApiService from '../../api/ApiService'
import '../../css/bannerbutton.scss'

// Import Bootstrap CSS and JS (add this to your main App.js or index.js if not already included)
import 'bootstrap/dist/css/bootstrap.min.css'
import 'bootstrap/dist/js/bootstrap.bundle.min.js'

const Banner = ({ scheduleRef }) => {
  const navigate = useNavigate()
  const bannerRef = useRef()
  const splideRef = useRef()
  const carouselRef = useRef()
  const [banner, setBanner] = useState(null)
  const [isMobile, setIsMobile] = useState(false)
  const [isTablet, setIsTablet] = useState(false)
  const [homepagebannerdata, setHomeSchedule] = useState(null)
  const [programSchedule, setProgramSchedule] = useState(null)
  const [imagesLoaded, setImagesLoaded] = useState(false)

  // ✅ Professional device detection
  useEffect(() => {
    const updateDeviceType = () => {
      const width = window.innerWidth
      setIsMobile(width <= 768)
      setIsTablet(width > 768 && width <= 1024)
    }

    updateDeviceType()
    window.addEventListener("resize", updateDeviceType)
    return () => window.removeEventListener("resize", updateDeviceType)
  }, [])

  const fetchBanner = async (location) => {
    try {
      let res = await ApiService.fetchBanner(location)
      setBanner(res)
    } catch (error) {
      console.error("Error fetching banner:", error)
    }
  }

  const scrollToSchedule = useCallback(() => {
    if (scheduleRef?.current) {
      const yOffset = -200
      const y = scheduleRef.current.getBoundingClientRect().top + window.pageYOffset + yOffset
      window.scrollTo({ top: y, behavior: "smooth" })
    }
  }, [scheduleRef])

  const fetchHomepageBannerData = async () => {
    try {
      let res = await ApiService.fetchHomepageBannerData()

      if (res?.homeSchedules?.length > 0) {
        setHomeSchedule(res.homeSchedules)
        // Preload images for better performance
        preloadImages(res.homeSchedules)
      }

      if (res?.programSchedules?.length > 0) {
        setProgramSchedule(res.programSchedules[0])
      }
    } catch (error) {
      console.error("Error fetching homepage banner data:", error)
      setBanner([])
    }
  }

  // ✅ Professional image preloading
  const preloadImages = (schedules) => {
    const imagePromises = schedules.map(item => {
      return new Promise((resolve, reject) => {
        const img = new Image()
        const imageUrl = isMobile
          ? `${process.env.REACT_APP_S3_URL}/uploads/home_sheduled/${item.homepageimagemobile}`
          : `${process.env.REACT_APP_S3_URL}/uploads/home_sheduled/${item.homepageimage}`

        img.onload = () => resolve(imageUrl)
        img.onerror = () => reject(imageUrl)
        img.src = imageUrl
      })
    })

    Promise.allSettled(imagePromises).then(() => {
      setImagesLoaded(true)
    })
  }

  // ✅ Professional button click handler (NO javascript:void(0))
  const handleButtonClick = useCallback((e, button, item) => {
    e.preventDefault()
    e.stopPropagation()

    // External URL
    if (button.slug?.startsWith('http://') || button.slug?.startsWith('https://')) {
      window.open(button.slug, '_blank', 'noopener,noreferrer')
      return
    }

    // Anchor link
    if (button.slug?.startsWith('#')) {
      if (button.slug === '#schedule' && scheduleRef?.current) {
        scrollToSchedule()
        return
      }
      const element = document.querySelector(button.slug)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' })
        return
      }
    }

    // Internal route
    if (button.slug) {
      const path = button.slug.startsWith('/') ? button.slug : `/${button.slug}`
      navigate(path)
    }
  }, [navigate, scheduleRef, scrollToSchedule])

  // ✅ Professional responsive position calculation
  const calculateResponsivePosition = useCallback((position, isImage = false) => {
    if (!position) {
      return isImage
        ? { x: 50, y: 50, width: 200, height: 200 }
        : { x: 50, y: 50 }
    }

    const viewportWidth = window.innerWidth

    if (isImage) {
      // Scale images based on viewport
      const ADMIN_CANVAS_WIDTH = 1600
      const currentBannerWidth = Math.min(viewportWidth, 1920)
      const scaleFactor = currentBannerWidth / ADMIN_CANVAS_WIDTH

      // Additional scaling for mobile/tablet
      const deviceScaleFactor = isMobile ? 0.8 : isTablet ? 0.9 : 1

      return {
        x: position.x,
        y: position.y,
        width: position.width * scaleFactor * deviceScaleFactor,
        height: position.height * scaleFactor * deviceScaleFactor
      }
    } else {
      // Direct percentage mapping for buttons
      return {
        x: position.x,
        y: position.y
      }
    }
  }, [isMobile, isTablet])

  // ✅ Professional button rendering with accessibility
  const renderPositionedButtons = useCallback((buttons, item) => {
    if (!buttons || !Array.isArray(buttons) || buttons.length === 0) {
      return null
    }

    return (
      <div className="positioned-buttons-overlay" role="group" aria-label="Banner actions">
        {buttons.map((button, index) => {
          const rawPosition = isMobile ? button.mobilePosition : button.desktopPosition
          const position = calculateResponsivePosition(rawPosition, false)

          const positionX = position?.x || 50
          const positionY = position?.y || 50

          // Professional responsive scaling
          const viewportWidth = window.innerWidth
          const baseFontSize = button.fontSize || 16
          const baseScale = viewportWidth / 1600

          let fontScale = baseScale
          if (isMobile) fontScale = Math.max(baseScale * 0.75, 0.7)
          else if (isTablet) fontScale = Math.max(baseScale * 0.85, 0.8)

          const responsiveFontSize = Math.max(Math.min(baseFontSize * fontScale, baseFontSize), 12)

          const basePaddingX = button.paddingX || 24
          const basePaddingY = button.paddingY || 12
          const responsivePaddingX = Math.max(basePaddingX * fontScale, 12)
          const responsivePaddingY = Math.max(basePaddingY * fontScale, 6)

          return (
            <button
              key={`btn-${index}-${button.name}-${item.slug || item.title}`}
              className="positioned-banner-button"
              onClick={(e) => handleButtonClick(e, button, item)}
              aria-label={button.name}
              type="button"
              style={{
                position: 'absolute',
                left: `${positionX}%`,
                top: `${positionY}%`,
                transform: 'translate(-50%, -50%)',

                backgroundColor: button.bgColor || '#3498db',
                color: button.textColor || '#ffffff',
                border: `${button.borderWidth || 2}px solid ${button.borderColor || '#2980b9'}`,
                fontSize: `${responsiveFontSize}px`,
                padding: `${responsivePaddingY}px ${responsivePaddingX}px`,
                borderRadius: `${button.borderRadius || 6}px`,

                cursor: 'pointer',
                zIndex: 10,
                pointerEvents: 'auto',
                transition: 'all 0.3s ease',
                fontWeight: '600',
                textDecoration: 'none',
                display: 'inline-block',
                minWidth: 'fit-content',
                whiteSpace: 'nowrap',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',

                // Accessibility
                outline: 'none',
                WebkitTapHighlightColor: 'transparent',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translate(-50%, -50%) scale(1.05)'
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.25)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translate(-50%, -50%) scale(1)'
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)'
              }}
              onFocus={(e) => {
                e.currentTarget.style.outline = '2px solid currentColor'
                e.currentTarget.style.outlineOffset = '2px'
              }}
              onBlur={(e) => {
                e.currentTarget.style.outline = 'none'
              }}
            >
              {button.name}
            </button>
          )
        })}
      </div>
    )
  }, [isMobile, isTablet, calculateResponsivePosition, handleButtonClick])

  // ✅ Professional overlay image rendering
  const renderPositionedOverlayImages = useCallback((overlayImages, item) => {
    if (!overlayImages || !Array.isArray(overlayImages) || overlayImages.length === 0) {
      return null
    }

    return (
      <div className="positioned-overlay-images" aria-hidden="true">
        {overlayImages.map((overlay, index) => {
          const rawPosition = isMobile ? overlay.mobilePosition : overlay.desktopPosition
          const position = calculateResponsivePosition(rawPosition, true)

          const positionX = position?.x || 50
          const positionY = position?.y || 50
          const width = isMobile
            ? (rawPosition.width || 150)
            : (rawPosition.width || 200)
          const height = isMobile
            ? 'auto'
            : (rawPosition.height || 200)

          const borderRadius = overlay.borderRadius || 4
          const imageUrl = `${process.env.REACT_APP_S3_URL}/uploads/home_sheduled/overlays/${overlay.imageUrl}`

          return (
            <div
              key={`overlay-${index}-${overlay.name}-${item.slug || item.title}`}
              className="positioned-overlay-image"
              style={{
                position: 'absolute',
                left: `${positionX}%`,
                top: `${positionY}%`,
                transform: 'translate(-50%, -50%)',

                width: `${width}px`,
                height: height === 'auto' ? 'auto' : `${height}px`,

                borderRadius: `${borderRadius}px`,
                background: "transparent", 
                zIndex: 8,
                pointerEvents: 'auto',
                overflow: 'visible',
              }}
            >
              <a
                href={overlay.slug || '#'}
                target={overlay.slug?.startsWith('http') ? "_blank" : "_self"}
                rel="noopener noreferrer"
                style={{
                  display: "block",
                  width: "100%",
                  height: "100%",
                  borderRadius: `${borderRadius}px`,
                  overflow: "hidden",
                }}
              >
                <img
                  src={imageUrl}
                  alt={overlay.name || ''}
                  loading="eager"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    display: 'block',
                    borderRadius: `${borderRadius}px`,
                  }}
                  onError={(e) => {
                    console.error(`Failed to load overlay image: ${imageUrl}`)
                    e.currentTarget.style.display = 'none'
                  }}
                />
              </a>
            </div>
          )
        })}
      </div>
    )
  }, [isMobile, isTablet, calculateResponsivePosition])

  useEffect(() => {
    fetchHomepageBannerData()
  }, [])

  // ✅ Bootstrap Carousel Initialization - THIS IS THE KEY FIX
  useEffect(() => {
    if (isMobile && filteredBanners.length > 1 && imagesLoaded) {
      // Wait for DOM to be ready
      const timer = setTimeout(() => {
        const carouselElement = document.getElementById('bannerCarousel')
        if (carouselElement && typeof window !== 'undefined' && window.bootstrap) {
          // Dispose any existing carousel instance
          const existingCarousel = window.bootstrap.Carousel.getInstance(carouselElement)
          if (existingCarousel) {
            existingCarousel.dispose()
          }

          // Create new carousel instance
          const carousel = new window.bootstrap.Carousel(carouselElement, {
            interval: 5000,
            ride: 'carousel',
            pause: false,
            keyboard: true,
            touch: true,
            wrap: true
          })

          // Store reference for cleanup
          carouselRef.current = carousel

          console.log('Carousel initialized successfully')
        } else {
          console.warn('Bootstrap not loaded or carousel element not found')
        }
      }, 100)

      return () => {
        clearTimeout(timer)
        if (carouselRef.current) {
          carouselRef.current.dispose()
          carouselRef.current = null
        }
      }
    }
  }, [isMobile, imagesLoaded])

  // ✅ Refresh Splide when data changes
  useEffect(() => {
    if (splideRef.current && imagesLoaded && homepagebannerdata && !isMobile) {
      setTimeout(() => {
        splideRef.current?.splide?.refresh()
      }, 100)
    }
  }, [imagesLoaded, homepagebannerdata, isMobile])

  useLayoutEffect(() => {
    fetchBanner('homepage')
    gsap.registerPlugin(ScrollTrigger)

    const scrollTrigger = ScrollTrigger.create({
      trigger: bannerRef.current,
      start: 'top top',
      pin: true,
      pinSpacing: false
    })

    return () => {
      scrollTrigger?.kill()
    }
  }, [])

  const banner_img = homepagebannerdata
    ? homepagebannerdata.map((item) => ({
      id: item.slug || item.title || `banner-${Date.now()}`,
      image: isMobile
        ? `${process.env.REACT_APP_S3_URL}/uploads/home_sheduled/${item.homepageimagemobile}`
        : `${process.env.REACT_APP_S3_URL}/uploads/home_sheduled/${item.homepageimage}`,
      type: item.type || "schedule",
      data: item,
    }))
    : []

  const filteredBanners = banner_img

  // ✅ Professional Splide Configuration
  const splideOptions = {
    type: 'loop',
    perPage: 1,
    perMove: 1,
    autoplay: filteredBanners.length > 1,
    interval: 10000,
    pauseOnHover: false,
    pauseOnFocus: false,
    speed: 800,
    easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
    arrows: false,
    pagination: false,
    drag: filteredBanners.length > 1,
    keyboard: 'global',
    gap: 0,
    width: '100%',
    height: 'auto',
    cover: false,
    lazyLoad: false,
    preloadPages: 3,
    accessibility: true,
    reducedMotion: {
      speed: 0,
      autoplay: true,
    },
  }

  if (!filteredBanners || filteredBanners.length === 0) {
    return null
  }

  const aspectRatio = (685 / 783) * 100;

  return (
    <section className="p-0 banner-section-wrapper mb-0" id="home-top" ref={bannerRef}>
      <div className="home-top"></div>
      {isMobile ? (
        <div
          id="bannerCarousel"
          className="carousel slide"
          data-bs-ride="carousel"
          data-bs-interval="5000"
          data-bs-touch="true"
          data-bs-keyboard="true"
          data-bs-pause="false"
        >
          <div className="carousel-inner">
            {filteredBanners.map((item, index) => (
              <div
                key={item.id}
                className={`carousel-item ${index === 0 ? "active" : ""}`}
              >
                <div
                  className="banner-slide position-relative"
                  style={{
                    backgroundImage: `url(${item.image})`,
                    backgroundSize: "contain",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                    overflow: "hidden",
                    paddingTop: `${aspectRatio}%`,
                    width: "100%" // Ensure full width
                  }}
                  role="img"
                  aria-label={item.data?.title || `Banner slide ${index + 1}`}
                >
                  {/* Overlay Images */}
                  {item.data?.overlayImages && (
                    renderPositionedOverlayImages(item.data.overlayImages, item.data)
                  )}

                  {/* Positioned Buttons */}
                  {item.data?.buttons && (
                    renderPositionedButtons(item.data.buttons, item.data)
                  )}

                  {/* Traditional Carousel Caption (Hidden when using positioned elements) */}
                  <div className="carousel-caption d-none">
                    {item.type === "darshan" && (
                      <div className="banner-content-janmashtami">
                        <div className="title">
                          <span>DISSOLVE IN KRISHNA CONSCIOUSNESS</span>
                        </div>
                        {item.data && (
                          <div className="box">
                            <p className="ptitle">{item.data.title}</p>
                            <p className="p1">
                              {new Date(item.data.date).toLocaleDateString("en-GB", {
                                day: "numeric",
                                month: "long",
                                year: "numeric",
                                weekday: "long",
                              })}
                            </p>
                            <p className="p2">{item.data.description}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Navigation Controls - Show only if more than 1 slide */}
          {filteredBanners.length > 1 && (
            <>
              {/* Previous/Next Buttons */}
              <button
                className="carousel-control-prev"
                type="button"
                data-bs-target="#bannerCarousel"
                data-bs-slide="prev"
                aria-label="Previous slide"
              >
                <span className="carousel-control-prev-icon" aria-hidden="true"></span>
                <span className="visually-hidden">Previous</span>
              </button>

              <button
                className="carousel-control-next"
                type="button"
                data-bs-target="#bannerCarousel"
                data-bs-slide="next"
                aria-label="Next slide"
              >
                <span className="carousel-control-next-icon" aria-hidden="true"></span>
                <span className="visually-hidden">Next</span>
              </button>

              {/* Indicators */}
              {/* <div className="carousel-indicators">
                {filteredBanners.map((_, index) => (
                  <button
                    key={index}
                    type="button"
                    data-bs-target="#bannerCarousel"
                    data-bs-slide-to={index}
                    className={index === 0 ? "active" : ""}
                    aria-current={index === 0 ? "true" : "false"}
                    aria-label={`Slide ${index + 1}`}
                  />
                ))}
              </div> */}
            </>
          )}
        </div>
      ) : (
        <Splide
          ref={splideRef}
          options={splideOptions}
          aria-label="Homepage Banner Carousel"
          className="banner-splide"
        >
          {filteredBanners.map((item, index) => (
            <SplideSlide key={item.id}>
              <div
                className="banner-slide position-relative"
                style={{
                  backgroundImage: `url(${item.image})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                  overflow: "hidden",
                }}
                role="img"
                aria-label={item.data?.title || `Banner slide ${index + 1}`}
              >
                {/* Overlay Images */}
                {item.data?.overlayImages && (
                  renderPositionedOverlayImages(item.data.overlayImages, item.data)
                )}

                {/* Positioned Buttons */}
                {item.data?.buttons && (
                  renderPositionedButtons(item.data.buttons, item.data)
                )}
              </div>
            </SplideSlide>
          ))}
        </Splide>
      )}
    </section>
  )
}

export default Banner
